import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, PartyPopper, Music2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import PageHeader from '../components/PageHeader';
import SectionHeading from '../components/SectionHeading';
import { Loader } from '../components/ui/loader';
import EmptyState from '../components/EmptyState';
import { API_URL } from '../utils/api';
import { getEventImage } from '../utils/eventImages';

export default function WeekendsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWeekendEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/weekends`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Error fetching weekend events:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeekendEvents();
  }, [fetchWeekendEvents]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    const startStr = startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const endStr = endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    
    return `${startStr} - ${endStr}`;
  };

  const getLowestPrice = (roomOptions) => {
    if (!roomOptions || roomOptions.length === 0) return null;
    const prices = roomOptions.map(r => r.price_per_person).filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  if (loading) {
    return <Loader fullScreen label="Loading weekend events" />;
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      {/* Hero Section */}
      <PageHeader
        align="center"
        eyebrow={
          <span className="inline-flex items-center gap-1">
            <PartyPopper className="w-3 h-3" />
            Dance Holidays
          </span>
        }
        title="Line Dance Weekend Events"
        subtitle="Join us for unforgettable line dance weekends at beautiful venues across the UK. Dance, socialise, and create lasting memories!"
      />

      {/* Events Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {events.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <EmptyState
                icon={Music2}
                title="No Upcoming Events"
                description="Check back soon for exciting line dance weekend events!"
              />
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.map((event, index) => (
                <motion.div
                  key={event.event_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/weekends/${event.slug}`}
                    className="block group"
                    data-testid={`weekend-event-card-${event.slug}`}
                  >
                    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:border-violet-300 transition-all duration-300 hover:shadow-lg">
                      {/* Event Image */}
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={getEventImage(event.event_id, event.image_url)}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        
                        {/* Date Badge Overlay */}
                        <div className="absolute top-4 left-4">
                          <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 text-center shadow-lg">
                            <div className="text-xs text-slate-700 font-medium">
                              {formatDateRange(event.start_date, event.end_date)}
                            </div>
                          </div>
                        </div>

                        {/* Price Badge */}
                        {getLowestPrice(event.room_options) && (
                          <div className="absolute top-4 right-4">
                            <Badge className="bg-green-500/90 text-white border-0">
                              From £{getLowestPrice(event.room_options)} pp
                            </Badge>
                          </div>
                        )}
                      </div>

                      {/* Event Details */}
                      <div className="p-5">
                        <h3 className="text-xl font-semibold text-stone-900 mb-2 group-hover:text-violet-700 transition-colors">
                          {event.title}
                        </h3>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-stone-600 text-sm">
                            <MapPin className="w-4 h-4 mr-2 text-violet-600" />
                            {event.venue_name}
                          </div>
                          <div className="flex items-center text-stone-600 text-sm">
                            <Calendar className="w-4 h-4 mr-2 text-violet-600" />
                            {formatDateRange(event.start_date, event.end_date)}
                          </div>
                          {event.max_capacity && (
                            <div className="flex items-center text-stone-600 text-sm">
                              <Users className="w-4 h-4 mr-2 text-violet-600" />
                              {event.current_bookings || 0} / {event.max_capacity} booked
                            </div>
                          )}
                        </div>

                        <p className="text-stone-600 text-sm line-clamp-2 mb-4">
                          {event.description}
                        </p>

                        <Button 
                          className="w-full bg-violet-600 hover:bg-violet-500"
                          data-testid={`book-weekend-${event.slug}`}
                        >
                          View Details & Book
                        </Button>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-[var(--bg-surface)] border-t border-stone-200">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <SectionHeading
              as="h2"
              align="center"
              title="Why Choose Our Dance Weekends?"
              className="mb-4"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="p-6 bg-white border border-stone-200 rounded-xl">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Music2 className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="text-stone-900 font-semibold mb-2">Expert Instruction</h3>
                <p className="text-stone-600 text-sm">
                  Learn from top instructors with workshops for all skill levels
                </p>
              </div>
              <div className="p-6 bg-white border border-stone-200 rounded-xl">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="text-stone-900 font-semibold mb-2">Great Community</h3>
                <p className="text-stone-600 text-sm">
                  Meet fellow dancers and make friends for life
                </p>
              </div>
              <div className="p-6 bg-white border border-stone-200 rounded-xl">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="text-stone-900 font-semibold mb-2">Beautiful Venues</h3>
                <p className="text-stone-600 text-sm">
                  Carefully selected locations with excellent facilities
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
